import React, { useEffect, useRef } from "react";
import { injectIntl, intlShape } from "react-intl";
import PropTypes from "prop-types";

import { FieldAPI, ReportAPI, ReportsModels } from "@ai360/core";

import { Checkbox, DateInput, NoLink, Section, SelectInput, SubSection } from "~/core";
import { IOption, ReportOptionsComponent, ReportOptionsComponentProps } from ".";

import { selectors as picklistSelectors, picklistNames, ACTIVE_YN } from "~/core/picklist";
import { getSelectedRecGuidSet } from "~/action-panel/components/rec-module/components/rec-list/selectors";
import { getSelectedEventGuidSet } from "~/action-panel/components/event-module/components/event-list/selectors";
import { getEventGeneralGuidToEventSummaryMap } from "~/recs-events/events/selectors";
import { useAppSelector } from "~/store/hooks";
import { messages } from "~/reports/i18n-messages";
import { model } from "~/reports/data";
import CroppingSeason from "~/reports/components/inputs/cropping-season";
import { getReportDataByReportType, getReportTypePages } from "~/reports/data/selectors";

import {
    getBasemapOptions,
    getLabelSizeOptions,
    getOrientationOptions,
    getPerReportOptions,
} from "../utils";
import { keywords } from "../keywords";
import { ReportPageSelection } from "../components/report-page-selection";

type SkySoilTestReportProps = {
    selectedCrop: FieldAPI.ICrop | null;
};

function SkySoilTestReport({
    hideDefaultOptions,
    intl: { formatMessage },
    onInputChange,
    initialPreferences,
}: ReportOptionsComponentProps & SkySoilTestReportProps) {
    const [showPageSelection, setShowPageSelection] = React.useState<boolean>(false);

    const reportData = useAppSelector(getReportDataByReportType(SkySoilTestReport.reportName));
    const orientationOptions: IOption[] = getOrientationOptions(formatMessage);
    const selectedEventGuidList = useAppSelector(getSelectedEventGuidSet);

    const selectedRecGuidList = useAppSelector(getSelectedRecGuidSet);

    const agEventSummaryMap = useAppSelector(getEventGeneralGuidToEventSummaryMap);
    const seasons = useAppSelector((state) =>
        picklistSelectors.getPicklistOptionsFromCode(
            state,
            picklistNames.getPickListCode(picklistNames.PICKLIST_CROPPING_SEASON)
        )
    );
    const seasonsToDisplay = useRef<string[]>();

    const basemapOptions = useAppSelector(getBasemapOptions);
    const optionsDisabled = selectedEventGuidList.size > 0 || selectedRecGuidList.size > 0;
    const croppingSeasonDisabled =
        reportData[model.PROPS_START_DATE] ||
        reportData[model.PROPS_END_DATE] ||
        selectedEventGuidList.size > 0 ||
        selectedRecGuidList.size > 0;
    const perReportOptions: IOption[] = getPerReportOptions(formatMessage);
    const labelSizeOptions: IOption[] = getLabelSizeOptions();

    useEffect(() => {
        onInputChange(model.PROPS_SELECTED_EVENT_GUID_LIST, [...selectedEventGuidList]);

        if (hideDefaultOptions) {
            return;
        }
        if (selectedEventGuidList.size > 0) {
            const seasonGuids = [];
            for (const eventGeneralGuid of selectedEventGuidList) {
                const event = agEventSummaryMap.get(eventGeneralGuid);
                if (event.agEventTypeName === keywords.samplingSoil) {
                    seasonGuids.push(event.croppingSeasonGuid);
                }
            }
            const seasonsText = [];
            for (const seasonGuid of seasonGuids) {
                const selectedSeason = seasons.find((season) => season.value === seasonGuid);
                if (seasonsText.indexOf(selectedSeason.label) === -1) {
                    seasonsText.push(selectedSeason.label);
                }
            }
            seasonGuids.sort();
            seasonsToDisplay.current = seasonsText;
        } else {
            seasonsToDisplay.current = [];
        }
    }, [selectedEventGuidList, selectedRecGuidList]);

    const disableUseOverviewMap =
        !reportData[model.PROPS_DISPLAY_SAMPLE_SITES] ||
        reportData[model.PROPS_ORIENTATION] === ReportsModels.PageOrientation.PORTRAIT;

    const disableMultiMapOption = reportData[model.PROPS_PER_REPORT_OPTION] !== "field";
    const pageSelectionTitle = formatMessage(messages.pageSelection);

    const reportTypePages = useAppSelector(getReportTypePages);
    const reportPageTypePrefs = initialPreferences[model.PROPS_REPORT_TYPE_PAGE_SELECTION]
        ? JSON.parse(initialPreferences[model.PROPS_REPORT_TYPE_PAGE_SELECTION])
        : {};
    const reportPageSelection = reportData[model.PROPS_REPORT_TYPE_PAGE_SELECTION]
        ? reportData[model.PROPS_REPORT_TYPE_PAGE_SELECTION]
        : {};

    return (
        <div className="report-options">
            <Section>
                {hideDefaultOptions ? null : (
                    <React.Fragment>
                        <SubSection>
                            <CroppingSeason
                                disabled={croppingSeasonDisabled}
                                required={!croppingSeasonDisabled}
                                reportType={SkySoilTestReport.reportName}
                                valuesToDisplay={seasonsToDisplay.current}
                            />
                        </SubSection>
                        <SubSection>
                            <DateInput
                                disabled={optionsDisabled}
                                placeholderText={formatMessage(messages.startDate)}
                                onChange={(value) => onInputChange(model.PROPS_START_DATE, value)}
                                timeFormat={false}
                                value={reportData[model.PROPS_START_DATE]}
                            />
                            <DateInput
                                disabled={optionsDisabled}
                                placeholderText={formatMessage(messages.endDate)}
                                onChange={(value) => onInputChange(model.PROPS_END_DATE, value)}
                                timeFormat={false}
                                value={reportData[model.PROPS_END_DATE]}
                            />
                        </SubSection>
                    </React.Fragment>
                )}
                <SubSection>
                    <SelectInput
                        required
                        containerClassNames={["display-options-list"]}
                        clearable={false}
                        placeholderText={formatMessage(messages.orientation)}
                        options={orientationOptions}
                        onChange={(value) => onInputChange(model.PROPS_ORIENTATION, value)}
                        value={reportData[model.PROPS_ORIENTATION]}
                    />
                    <SelectInput
                        required
                        clearable={false}
                        optionIsHiddenKey={ACTIVE_YN}
                        containerClassNames={["display-options-list"]}
                        placeholderText={formatMessage(messages.labelSize)}
                        options={labelSizeOptions}
                        onChange={(value) => onInputChange(model.PROPS_LABEL_SIZE, value)}
                        value={reportData[model.PROPS_LABEL_SIZE]}
                    />
                </SubSection>
                <SubSection>
                    <SelectInput
                        required
                        optionIsHiddenKey={ACTIVE_YN}
                        containerClassNames={["display-options-list"]}
                        clearable={false}
                        placeholderText={formatMessage(messages.selectedBasemap)}
                        options={basemapOptions}
                        onChange={(value) => onInputChange(model.PROPS_SELECTED_BASEMAP, value)}
                        value={reportData[model.PROPS_SELECTED_BASEMAP]}
                    />
                    <SelectInput
                        required
                        clearable={false}
                        containerClassNames={["display-options-list"]}
                        placeholderText={formatMessage(messages.perReportOptions)}
                        options={perReportOptions}
                        onChange={(value) => onInputChange(model.PROPS_PER_REPORT_OPTION, value)}
                        value={reportData[model.PROPS_PER_REPORT_OPTION]}
                    />
                </SubSection>
                <SubSection>
                    <Checkbox
                        containerClassNames={["custom-imagery"]}
                        onChange={(e, value) =>
                            onInputChange(model.PROPS_USE_CUSTOM_IMAGERY, value)
                        }
                        value={reportData[model.PROPS_USE_CUSTOM_IMAGERY]}
                        label={formatMessage(messages.useCustomImagery)}
                    />
                    <Checkbox
                        disabled={disableUseOverviewMap}
                        onChange={(e, value) => onInputChange(model.PROPS_USE_OVERVIEW_MAP, value)}
                        value={reportData[model.PROPS_USE_OVERVIEW_MAP]}
                        label={formatMessage(messages.showOverviewMap)}
                    />
                </SubSection>
                <SubSection>
                    <Checkbox
                        onChange={(e, value) =>
                            onInputChange(model.PROPS_DISPLAY_SAMPLE_SITES, value)
                        }
                        value={reportData[model.PROPS_DISPLAY_SAMPLE_SITES]}
                        label={formatMessage(messages.displaySampleSites)}
                    />
                    <Checkbox
                        disabled={disableMultiMapOption}
                        onChange={(e, value) =>
                            onInputChange(model.PROPS_MULTIPLE_MAPS_PER_PAGE, value)
                        }
                        value={reportData[model.PROPS_MULTIPLE_MAPS_PER_PAGE]}
                        label={formatMessage(messages.multipleMapsPerPage)}
                    />
                </SubSection>
                <SubSection>
                    <Checkbox
                        onChange={(e, value) => onInputChange(model.PROPS_ORIGINAL_FORMAT, value)}
                        value={reportData[model.PROPS_ORIGINAL_FORMAT]}
                        label={formatMessage(messages.originalFormat)}
                    />
                    <NoLink
                        className="categories-link report-options-modal-link page-selection"
                        label={pageSelectionTitle}
                        onClick={() => setShowPageSelection(true)}
                    />
                    <ReportPageSelection
                        isOpen={showPageSelection}
                        onChange={(value) => {
                            onInputChange(model.PROPS_REPORT_TYPE_PAGE_SELECTION, value);
                            setShowPageSelection(false);
                        }}
                        onClose={() => setShowPageSelection(false)}
                        title={formatMessage(messages.pageSelection)}
                        reportTypePages={reportTypePages}
                        reportTypePagesPrefs={reportPageTypePrefs}
                        selectedReportTypes={[SkySoilTestReport.reportName]}
                        selectedReportTypePages={reportPageSelection}
                    />
                </SubSection>
            </Section>
        </div>
    );
}

SkySoilTestReport.propTypes = {
    intl: intlShape,
    reportPreferences: PropTypes.object,
    selectedCrop: null,
};

SkySoilTestReport.defaultReportOptions = {
    [model.PROPS_REPORT_TYPE_PAGE_SELECTION]: {},
    [model.PROPS_SELECTED_EVENT_GUID_LIST]: [],
    [model.PROPS_CROPPING_SEASON_GUID]: null,
    [model.PROPS_START_DATE]: null,
    [model.PROPS_END_DATE]: null,
    [model.PROPS_ORIENTATION]: "landscape",
    [model.PROPS_LABEL_SIZE]: 3,
    [model.PROPS_SELECTED_BASEMAP]: "reportImagery",
    [model.PROPS_PER_REPORT_OPTION]: "field",
    [model.PROPS_USE_CUSTOM_IMAGERY]: false,
    [model.PROPS_USE_OVERVIEW_MAP]: false,
    [model.PROPS_DISPLAY_SAMPLE_SITES]: false,
    [model.PROPS_MULTIPLE_MAPS_PER_PAGE]: false,
    [model.PROPS_ORIGINAL_FORMAT]: false,
};

SkySoilTestReport.reportPreferences = [
    model.PROPS_REPORT_TYPE_PAGE_SELECTION,
    model.PROPS_PER_REPORT_OPTION,
    model.PROPS_SELECTED_BASEMAP,
    model.PROPS_USE_OVERVIEW_MAP,
    model.PROPS_USE_CUSTOM_IMAGERY,
    model.PROPS_ORIENTATION,
    model.PROPS_DISPLAY_SAMPLE_SITES,
    model.PROPS_MULTIPLE_MAPS_PER_PAGE,
    model.PROPS_LABEL_SIZE,
];

SkySoilTestReport.reportName = ReportAPI.ReportNames.AGVANCE_SKY_NUTRIENT_ATLAS;

export default injectIntl(SkySoilTestReport) as ReportOptionsComponent<
    ReportOptionsComponentProps & SkySoilTestReportProps
>;
